import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
} from "@mui/material";
import PatientService from "../../services/PatientService";
import { format, parseISO, startOfMonth, endOfMonth, addMonths } from "date-fns";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { useTranslation } from "react-i18next";

interface MeasurementHistoryProps {
  patientId: string;
  conditionType: string;
  organization_id: string;
  token: string;
  open: boolean;
  onClose: () => void;
}

interface TableRowData {
  timestamp: string;
  measurements: { [measurementType: string]: number | undefined };
}

interface FormattedMeasurementEntry {
  key: string;
  label: string;
  value: string;
}

export const MeasurementListDialog: React.FC<MeasurementHistoryProps> = ({
  patientId,
  conditionType,
  organization_id,
  token,
  open,
  onClose,
}) => {
  const [loading, setLoading] = useState(true);
  const [historyData, setHistoryData] = useState<any>(null);
  // State for the currently displayed month.
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  // Cache measurement data per month keyed by "yyyy-MM"
  const cacheRef = useRef<{ [monthKey: string]: any }>({});
  const { t } = useTranslation();

  // Fetch measurements when dialog opens or selected month changes.
  useEffect(() => {
    if (open) {
      fetchMeasurementHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedDate]);

  const fetchMeasurementHistory = async () => {
    setLoading(true);
    const monthKey = format(selectedDate, "yyyy-MM");
    if (cacheRef.current[monthKey]) {
      setHistoryData(cacheRef.current[monthKey]);
      setLoading(false);
      return;
    }
    try {
      const startDate = format(startOfMonth(selectedDate), "yyyy-MM-dd");
      const endDate = format(endOfMonth(selectedDate), "yyyy-MM-dd");
      const response = await PatientService.getMeasurementHistory(
        token,
        patientId,
        organization_id,
        startDate,
        endDate
      );
      setHistoryData(response.history);
      // Cache the fetched history.
      cacheRef.current[monthKey] = response.history;
    } catch (error) {
      console.error("Error fetching measurement history:", error);
    }
    setLoading(false);
  };

  // Keyboard navigation for month (left/right arrow keys).
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft") {
        handlePrevMonth();
      } else if (event.key === "ArrowRight") {
        handleNextMonth();
      }
    };
    if (open) {
      window.addEventListener("keydown", handleKeyDown);
    }
    return () => window.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedDate]);

  const handlePrevMonth = () => {
    setSelectedDate((prev) => addMonths(prev, -1));
  };

  const handleNextMonth = () => {
    const nextMonth = addMonths(selectedDate, 1);
    const now = new Date();
    // Do not navigate if nextMonth is in the future.
    if (
      nextMonth.getFullYear() > now.getFullYear() ||
      (nextMonth.getFullYear() === now.getFullYear() && nextMonth.getMonth() > now.getMonth())
    ) {
      return;
    }
    setSelectedDate(nextMonth);
  };

  // Process historyData into table rows.
  const getTableData = (): TableRowData[] => {
    if (!historyData) return [];
    const rowMap: { [timestamp: string]: TableRowData } = {};

    Object.keys(historyData).forEach((measurementType) => {
      historyData[measurementType].forEach(([timestamp, value]: [string, number]) => {
        if (!rowMap[timestamp]) {
          rowMap[timestamp] = { timestamp, measurements: {} };
        }
        rowMap[timestamp].measurements[measurementType] = value;
      });
    });

    return Object.values(rowMap).sort(
      (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );
  };

  if (!open) return null;

  const tableData = getTableData();
  const formatMeasurementEntries = (
    measurements: { [measurementType: string]: number | undefined }
  ): FormattedMeasurementEntry[] => {
    const formatted: FormattedMeasurementEntry[] = [];
    const processed = new Set<string>();

    // Group related measurements
    const measurementGroups: { [key: string]: string[] } = {
      SpO2: ["oxygen_saturation", "perfusion_index"],
      PeakFlow: ["fev1", "pef"],
      BloodPressure: ["systolic", "diastolic", "mean_arterial_pressure"],
      HeartRate: ["heart_rate"],
    };

    // Mapping of field names to abbreviated labels
    const abbreviatedLabels: { [key: string]: string } = {
      oxygen_saturation: t("SpO2"),
      perfusion_index: t("PI"),
      fev1: t("FEV1"),
      pef: t("PEF"),
      heart_rate: t("HR"),
      systolic: t("Systolic"),
      diastolic: t("Diastolic"),
      mean_arterial_pressure: t("MAP"),
    };

    // Helper function to format values
    const formatValue = (value: number | undefined, measurementType: string): string => {
      if (value === undefined) return "--";
      
      // Round percentages to 2 decimal places, other values to reasonable precision
      if (measurementType === "oxygen_saturation" || measurementType === "perfusion_index") {
        return value.toFixed(2);
      }
      
      return value.toString();
    };

    // Process measurement groups first
    Object.entries(measurementGroups).forEach(([groupName, fields]) => {
      const groupValues: string[] = [];
      let hasValues = false;

      fields.forEach((field) => {
        if (measurements[field] !== undefined && !processed.has(field)) {
          const label = abbreviatedLabels[field] || field;
          const formattedValue = formatValue(measurements[field], field);
          groupValues.push(`${label}: ${formattedValue}`);
          processed.add(field);
          hasValues = true;
        }
      });

      if (hasValues) {
        formatted.push({
          key: groupName.toLowerCase(),
          label: groupName,
          value: groupValues.join(", "),
        });
      }
    });

    // Process remaining measurements that weren't grouped
    Object.entries(measurements).forEach(([measurementType, value]) => {
      if (!processed.has(measurementType) && value !== undefined) {
        const label = abbreviatedLabels[measurementType] || t(measurementType);
        const formattedValue = formatValue(value, measurementType);
        formatted.push({
          key: measurementType,
          label: label,
          value: formattedValue,
        });
      }
    });

    return formatted;
  };

  // Group tableData by day (using the ISO date portion).
  const groupedByDay = tableData.reduce((acc, row) => {
    const day = row.timestamp.split("T")[0];
    if (!acc[day]) {
      acc[day] = [];
    }
    acc[day].push(row);
    return acc;
  }, {} as { [day: string]: TableRowData[] });

  // Sort days in ascending order.
  const sortedDays = Object.keys(groupedByDay).sort(
    (a, b) => new Date(b).getTime() - new Date(a).getTime()
  );

  // Build table rows with merged cells.
  const tableRows: JSX.Element[] = [];
  sortedDays.forEach((day) => {
    const dayRows = groupedByDay[day];
    const formattedRows = dayRows.map((row) => ({
      row,
      entries: formatMeasurementEntries(row.measurements),
    }));
    const totalRowsForDay = formattedRows.reduce(
      (sum, { entries }) => sum + entries.length,
      0
    );
    if (totalRowsForDay === 0) {
      return;
    }
    let isFirstRowForDay = true;
    formattedRows.forEach(({ row, entries }) => {
      if (!entries.length) {
        return;
      }
      let isFirstRowForTime = true;
      entries.forEach((entry, index) => {
        tableRows.push(
          <TableRow key={`${row.timestamp}-${entry.key}-${index}`}>
            {isFirstRowForDay && isFirstRowForTime && (
              <TableCell
                rowSpan={totalRowsForDay}
                sx={{ verticalAlign: "top", fontWeight: "bold", py: 0.5 }}
              >
                {format(parseISO(row.timestamp), "MMM dd, yyyy")}
              </TableCell>
            )}
            {isFirstRowForTime && (
              <TableCell
                rowSpan={entries.length}
                sx={{ verticalAlign: "top", fontWeight: "bold", py: 0.5 }}
              >
                {format(parseISO(row.timestamp), "HH:mm")}
              </TableCell>
            )}
            <TableCell sx={{ py: 1 }}>
              {entry.label}: {entry.value}
            </TableCell>
          </TableRow>
        );
        isFirstRowForDay = false;
        isFirstRowForTime = false;
      });
    });
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      sx={{ "& .MuiDialog-container": { alignItems: "flex-start" } }}
      PaperProps={{
        sx: {
          mt: 12,
          maxHeight: "calc(100vh - 64px)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden", // Prevent dialog-level scrolling.
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" width="100%" alignItems="center">
          <Typography variant="h6" sx={{ textAlign: "left" }}>
            {t("Recorded Measurements")} - {patientId}
          </Typography>
          <Box display="flex" alignItems="center">
            <IconButton onClick={handlePrevMonth}>
              <ArrowBackIosIcon fontSize="small" />
            </IconButton>
            {/* Fixed-width container for date display */}
            <Box sx={{ width: "220px", textAlign: "center" }}>
              <Typography variant="h6">
                {format(selectedDate, "MMMM yyyy")}
              </Typography>
            </Box>
            <IconButton
              onClick={handleNextMonth}
              disabled={
                selectedDate.getFullYear() === new Date().getFullYear() &&
                selectedDate.getMonth() === new Date().getMonth()
              }
            >
              <ArrowForwardIosIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent
        sx={{
          flex: 1,
          p: 0,
          overflow: "hidden", // Prevent DialogContent from scrolling.
        }}
      >
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={100}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {tableRows.length > 0 ? (
              <TableContainer
                component={Paper}
                sx={{
                  flex: 1,
                  maxHeight: "calc(100vh - 200px)", // Set a defined max height.
                  overflowY: "auto", // Enable scrolling if necessary.
                }}
              >
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", width: "30%", py: 0.5 }}>
                        {t("Date")}
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold", width: "20%", py: 0.5 }}>
                        {t("Time")}
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold", py: 0.5 }}>
                        {t("Measurements")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>{tableRows}</TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="h6" sx={{ marginTop: 2, textAlign: "center" }}>
                {t("No Measurements Found")}
              </Typography>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("Close")}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default MeasurementListDialog;
